import React, {useEffect, useState} from 'react';
import { useNavigate } from 'react-router-dom';
import {
    Box,
    Typography,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    Paper,
    Button,
    Dialog,
    DialogContent,
    DialogTitle,
    useMediaQuery,
    useTheme, CircularProgress,
} from '@mui/material';
import QRCodeHelper from '../../Shared/QRCodeHelper';
import EntityState from "../../../graphql/queries/EntityState";
import LoadingOverlay from "../../Shared/LoadingOverlay";
import {useTranslation} from "react-i18next";
import ActionCancelReservation from "../../../graphql/mutations/ActionCancelReservation";
import {toast} from "react-toastify";

const Checkout = () => {
    const [openModal, setOpenModal] = useState(false);
    const [selectedBook, setSelectedBook] = useState(null);
    const navigate = useNavigate();
    const [data, setData] = useState([]);
    const [isLoading, setIsLoading] = useState(true);
    const { t } = useTranslation();

    useEffect(() => {
        loadEntities();
    }, []);

    const loadEntities = (after = null) => {
        setIsLoading(true);

        EntityState.fetch({ state: 'RESERVED' })
            .then((response) => {
                setData(response.entityState);
            })
            .catch((error) => {
                console.error('Error fetching entities:', error);
            })
            .finally(() => {
                setIsLoading(false);
            });
    };

    const handleOpenModal = (book) => {
        setSelectedBook(book);
        setOpenModal(true);
    };

    const handleCloseModal = () => {
        setSelectedBook(null);
        setOpenModal(false);
    };

    // Theme and responsive breakpoints
    const theme = useTheme();
    const isMobile = useMediaQuery(theme.breakpoints.down('sm'));
    const handleCancelReserve = ({ entity }) => {
        setIsLoading(true);

        try {
            ActionCancelReservation.commit({ id: entity.dbId }).then(({ actionCancelReservation }) => {
                const { error, entity } = actionCancelReservation;

                if (error.length > 0) {
                    toast.error(error);
                } else {
                    navigate('/catalogue')
                    toast.success(t("toaster.reservation_cancelled"));
                }
                setIsLoading(false);
            });
        } catch (err) {
            setIsLoading(false);
            toast.error(t('unexpectedError', 'An unexpected error occurred.'));
        }
    };

    return (
        <Box sx={{ p: 2, [theme.breakpoints.up('sm')]: { p: 4 } }}>
            <Typography variant="h4" gutterBottom>
                {t('profile.tabs.checkout.subtitle')}
            </Typography>
            <TableContainer component={Paper}>
                <Table>
                    <TableHead>
                        <TableRow>
                            <TableCell align="center">{t('profile.tabs.checkout.sku')}</TableCell>
                            <TableCell>{t('profile.tabs.checkout.bookTitle')}</TableCell>
                            {!isMobile && <TableCell>{t('profile.tabs.checkout.author')}</TableCell>} {/* Hide on mobile */}
                            <TableCell></TableCell>
                        </TableRow>
                    </TableHead>
                    <TableBody>
                        {data?.length < 1 && <LoadingOverlay isLoading={isLoading} />}
                        {data?.map((entity) => (
                            <TableRow
                                key={entity.id}
                                sx={{
                                    transition: 'background-color 0.3s ease',
                                    '&:hover': {
                                        backgroundColor: 'rgba(0, 0, 0, 0.04)', // Slightly darker background on hover
                                    },
                                }}
                            >
                                <TableCell style={{ color: '#fc3d03' }} align="center"><b>{entity.entity?.sku}</b></TableCell>
                                <TableCell>{entity.entity?.book?.title || ''}</TableCell>
                                {!isMobile && <TableCell>{entity.entity?.book?.authors?.map(a => a.name).join(', ') || ''}</TableCell>} {/* Hide on mobile */}
                                <TableCell>
                                    {/*<Button*/}
                                    {/*    variant="outlined"*/}
                                    {/*    color="primary"*/}
                                    {/*    size="small"*/}
                                    {/*    onClick={() => handleOpenModal(entity)}*/}
                                    {/*    sx={{*/}
                                    {/*        fontSize: isMobile ? '0.65rem' : '0.85rem',*/}
                                    {/*        padding: isMobile ? '2px 6px' : '4px 10px',*/}
                                    {/*        minWidth: 'unset', // optional: avoid default min width*/}
                                    {/*    }}*/}
                                    {/*>*/}
                                    {/*    QR-code*/}
                                    {/*</Button>*/}
                                    {/*<br />*/}
                                    <Button
                                        disabled={isLoading}
                                        onClick={() => handleCancelReserve(entity)}
                                        variant="outlined"
                                        color="primary"
                                        size="small"
                                        sx={{
                                            fontSize: isMobile ? '0.65rem' : '0.85rem',
                                            padding: isMobile ? '2px 6px' : '4px 10px',
                                            minWidth: 'unset',
                                            marginTop: '4px', // spacing between buttons
                                        }}
                                    >
                                        {isLoading && (
                                            <CircularProgress size={16} color="inherit" sx={{ marginRight: '8px' }} />
                                        )}
                                        {t('catalog.item.cancelReservation')}
                                    </Button>
                                </TableCell>
                            </TableRow>
                        ))}
                    </TableBody>
                </Table>
            </TableContainer>

            {/* QR Code Modal */}
            <Dialog
                open={openModal}
                onClose={handleCloseModal}
                aria-labelledby="qr-code-title"
                fullScreen={isMobile} // Fullscreen modal on mobile
            >
                <DialogTitle id="qr-code-title">
                    QR Code for {selectedBook?.entity?.book?.title || ''}
                </DialogTitle>
                <DialogContent sx={{ textAlign: 'center' }}>
                    {selectedBook && (
                        <>
                            <Typography variant="body1" sx={{ mb: 2 }}>
                                Scan the QR code below to access your library account:
                            </Typography>
                            <QRCodeHelper
                                value={`${process.env.REACT_APP_API_URL}/book/${selectedBook.dbId}`}
                                label="Scan the QR code below:"
                                size={150}
                            />
                            <Typography variant="body2" sx={{ mt: 2 }}>
                                {selectedBook?.entity?.book?.title || ''}
                            </Typography>
                        </>
                    )}
                    <Button
                        variant="outlined"
                        sx={{ mt: 3 }}
                        onClick={handleCloseModal}
                    >
                        {t('close')}
                    </Button>
                </DialogContent>
            </Dialog>
        </Box>
    );
};

export default Checkout;
